"use client";

import { useContext, useRef, useEffect, useCallback, useState } from "react";

import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import CategoryAccordionWrapper from "@components/swagmagic/dynamic/slices/category-list/category-accordion";

import { CATEGORY_HOME_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export interface CategoryListProps {
  categories: any[];
  linkRouteDefaults?: LinkRouteDefaults;
}

const CategoryList = ({ categories, linkRouteDefaults }: CategoryListProps) => {
  const { isDesktopDevice } = useContext(CurrentDeviceContext);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateScrollThumb = useCallback(() => {
    if (!scrollThumbRef.current || !scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    if (scrollWidth <= clientWidth) return;

    const thumbWidthPercent = (clientWidth / scrollWidth) * 100;
    scrollThumbRef.current.style.width = `${thumbWidthPercent}%`;

    const maxScroll = scrollWidth - clientWidth;
    const progress = scrollLeft / maxScroll;
    const trackWidth = scrollThumbRef.current.parentElement?.offsetWidth || 0;
    const thumbWidth = scrollThumbRef.current.offsetWidth;
    scrollThumbRef.current.style.transform = `translateX(${
      progress * (trackWidth - thumbWidth)
    }px)`;
  }, []);

  useEffect(() => {
    if (isDesktopDevice || !scrollContainerRef.current) {
      setIsScrollable(false);
      return;
    }

    const scrollContainer = scrollContainerRef.current;

    const checkScrollable = () => {
      setIsScrollable(scrollContainer.scrollWidth > scrollContainer.clientWidth);
    };

    updateScrollThumb();
    checkScrollable();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollThumb();
      checkScrollable();
    });
    resizeObserver.observe(scrollContainer);

    scrollContainer.addEventListener("scroll", updateScrollThumb);

    return () => {
      scrollContainer.removeEventListener("scroll", updateScrollThumb);
      resizeObserver.disconnect();
    };
  }, [isDesktopDevice, updateScrollThumb]);

  return (
    <>
      <CategoryAccordionWrapper
        categories={categories}
        linkRouteDefaults={linkRouteDefaults}
      />
      <div ref={scrollContainerRef} className={styles.categoryListMobile}>
        {categories.map((category) => {
          return (
            <LinkWithLang
              {...linkRouteDefaults}
              key={`${category.code}-${category.name}`}
              type={CATEGORY_HOME_LINK_TYPE}
              data={category}
              target={category?.linkToNewTab ? "_blank" : "_self"}
              className={styles.categoryOptions}
            >
              {category.name}
            </LinkWithLang>
          );
        })}
      </div>
      {isScrollable && (
        <div className={styles.scrollTrack}>
          <div ref={scrollThumbRef} className={styles.scrollThumb} />
        </div>
      )}
    </>
  );
};

export default CategoryList;
